import { type CompanyTechnologyNestedSelectDto, CompanyType, type TechnologySelectDto } from '@@types/api.types';
import { BadgeList } from '@components/shared/badge-list.component';
import { CompanyLogoPreview } from '@components/shared/company-logo-preview.component';
import { ExternalLink } from '@components/shared/external-link.component';
import { Tile } from '@components/shared/tile.component';
import { cn } from '@lib/utils';
import { buildCompanyLocationDisplay, formatCompanyType } from '@utils/company-display.util';
import { cmmcLevelOptions, countries } from '@utils/constants';
import { formatProper, formatUsd } from '@utils/text.util';
import { isValidUrl, toHttpsUrl } from '@utils/url.util';
import { Building2 } from 'lucide-react';
import { FC, ReactNode, useMemo } from 'react';

type TechnologyOrganizationTabProps = {
  technology?: TechnologySelectDto;
};

type InfoFieldProps = {
  fullWidth?: boolean;
  label: string;
  value?: ReactNode;
};

const InfoField: FC<InfoFieldProps> = (props) => {
  const { fullWidth = false, label, value } = props;

  const resolvedValue = value ?? '—';
  const isPlainValue = typeof resolvedValue === 'string' || typeof resolvedValue === 'number';

  return (
    <div
      className={cn('flex flex-col gap-1 min-w-0 overflow-hidden', fullWidth ? 'w-full' : 'w-full @[40rem]:w-[45%]')}
    >
      <span className="text-sm font-light text-gray-500">{label}</span>
      <div
        className={cn(
          'w-full text-sm font-normal text-gray-800 min-w-0 overflow-hidden',
          isPlainValue && !fullWidth && 'text-nowrap text-ellipsis',
          isPlainValue && fullWidth && 'line-clamp-12',
        )}
      >
        {resolvedValue}
      </div>
    </div>
  );
};

const Section: FC<{ children: ReactNode; className?: string }> = (props) => {
  const { children, className } = props;

  return (
    <div
      className={cn(
        'flex p-4 gap-x-12 gap-y-4 justify-between flex-wrap w-full border-t border-gray-300 border-solid',
        className,
      )}
    >
      {children}
    </div>
  );
};

const mapCountryCodeToName = (code?: string): string | undefined => {
  if (!code) return undefined;
  const normalizedCode = code.toUpperCase();
  return countries.find((country) => country.code === normalizedCode || country.name === code)?.name ?? code;
};

const formatContact = (contact?: { email?: string; name?: string }): ReactNode | undefined => {
  if (!contact) return undefined;
  const { email, name } = contact;
  if (!name && !email) return undefined;

  return (
    <div className="flex flex-col">
      {name && <span>{name}</span>}
      {email && (
        <a
          className="text-blue-600 hover:underline"
          href={`mailto:${email}`}
          rel="noreferrer noopener"
        >
          {email}
        </a>
      )}
    </div>
  );
};

type UrlField = {
  label: string;
  value: (company: CompanyTechnologyNestedSelectDto) => string | undefined;
};

const urlFields: UrlField[] = [
  { label: 'Website', value: (company) => company.urls?.website },
  { label: 'Twitter', value: (company) => company.urls?.twitter },
  { label: 'LinkedIn', value: (company) => company.urls?.linkedin },
  { label: 'Crunchbase', value: (company) => company.urls?.crunchbase },
];

export const TechnologyOrganizationTab: FC<TechnologyOrganizationTabProps> = (props) => {
  const { technology } = props;
  const company = technology?.company;

  const locationDisplay = useMemo(() => (company ? buildCompanyLocationDisplay(company) : undefined), [company]);
  const companyTypeLabel = useMemo(() => formatCompanyType(company?.type), [company?.type]);

  const hasUrls = useMemo(() => {
    if (!company) return false;
    return urlFields.some((field) => {
      const url = field.value(company);
      return url && isValidUrl(url);
    });
  }, [company]);

  const cmmcLabel = useMemo(() => {
    if (!company?.cmmcLevel) return undefined;
    return cmmcLevelOptions.find((option) => option.value === company.cmmcLevel)?.label;
  }, [company?.cmmcLevel]);

  const revenueDisplay = useMemo(() => {
    const revenue = company?.revenue;
    if (!revenue?.amount || !revenue?.period) return undefined;
    return `${formatUsd(revenue.amount, 0)} USD ${String(revenue.period).toLowerCase()}`;
  }, [company?.revenue]);

  const valuationDisplay = useMemo(() => {
    const valuation = company?.revenue?.valuation;
    if (!valuation) return undefined;
    return `${formatUsd(valuation, 0)} USD`;
  }, [company?.revenue?.valuation]);

  const previousGovWorkCountries = useMemo(() => {
    const codes = company?.previousGovernmentWorkCountries;
    if (!codes || codes.length === 0) return undefined;
    const names = codes.map((code) => mapCountryCodeToName(code)).filter((name): name is string => Boolean(name));
    if (names.length === 0) return undefined;
    return names;
  }, [company?.previousGovernmentWorkCountries]);

  const isResearch = company?.type === CompanyType.RESEARCH || company?.type === CompanyType.RESEARCH_PROJECT;
  const isCommercial = company?.type === CompanyType.COMMERCIAL;
  const researchData = company?.researchOrganizationData;
  const organizationTypeLabel = researchData?.type
    ? formatProper(researchData.type.toLowerCase().replace(/_/g, ' '))
    : undefined;
  const foreignAffiliates =
    researchData?.foreignAffiliates?.filter((affiliate) => affiliate && affiliate.trim().length > 0) ?? [];
  const hasForeignAffiliation = foreignAffiliates.length > 0;
  const hasFunding = Boolean(researchData?.funding?.type);
  const fundingTypeLabel = researchData?.funding?.type
    ? formatProper(researchData.funding.type.toLowerCase().replace(/_/g, ' '))
    : undefined;
  const fundingCountryBadges =
    researchData?.funding?.countries
      ?.map((code) => ({ id: code, name: mapCountryCodeToName(code) ?? code }))
      .filter((item) => Boolean(item.name)) ?? [];

  const hasFinancialData = Boolean(revenueDisplay || valuationDisplay || company?.isParentOrganization !== undefined);

  if (!company) {
    return (
      <div className="@container/technology-organization">
        <Tile
          emptyState={{ message: 'No organization data available for this technology.', title: 'No Organization' }}
          isEmpty
        />
      </div>
    );
  }

  const renderDetailsContent = (): ReactNode => {
    if (isResearch) {
      return (
        <div className="@container flex flex-col">
          {/* Basic info */}
          <Section>
            <InfoField
              label="Legal Name"
              value={company.name}
            />
            <InfoField
              label="Organization Type"
              value={organizationTypeLabel}
            />
            <InfoField
              label="Location"
              value={locationDisplay}
            />
            <InfoField
              label="Department"
              value={researchData?.departmentName}
            />
            <InfoField
              fullWidth
              label="Description"
              value={company.description}
            />
          </Section>

          {/* Contacts */}
          <Section>
            <InfoField
              label="Department Contact"
              value={formatContact(researchData?.departmentHeadContact)}
            />
            <InfoField
              label="Tech Transfer Contact"
              value={formatContact(researchData?.techTransferContact)}
            />
          </Section>

          {/* IP, Foreign Affiliations, Funding */}
          <Section>
            <InfoField
              label="Intellectual Property License Established"
              value={researchData?.ipLicense !== undefined ? (researchData.ipLicense ? 'Yes' : 'No') : undefined}
            />
            <InfoField
              fullWidth={hasForeignAffiliation}
              label="Foreign Affiliations"
              value={
                hasForeignAffiliation ? (
                  <BadgeList
                    items={foreignAffiliates.map((affiliate) => ({ id: affiliate, name: affiliate }))}
                    size="sm"
                  />
                ) : (
                  'No'
                )
              }
            />
            <InfoField
              label="Funding"
              value={hasFunding ? 'Yes' : 'No'}
            />
            {hasFunding && (
              <InfoField
                label="Funding Type"
                value={fundingTypeLabel}
              />
            )}
            {fundingCountryBadges.length > 0 && (
              <InfoField
                fullWidth
                label="Funding Countries"
                value={
                  <BadgeList
                    items={fundingCountryBadges}
                    size="sm"
                  />
                }
              />
            )}
          </Section>

          {/* Previous Government Work */}
          {previousGovWorkCountries && (
            <Section>
              <InfoField
                fullWidth
                label="Previous Government Work"
                value={
                  <BadgeList
                    items={previousGovWorkCountries.map((name) => ({ id: name, name }))}
                    size="sm"
                  />
                }
              />
            </Section>
          )}
        </div>
      );
    }

    // Commercial / default
    return (
      <div className="@container flex flex-col">
        {/* Basic info */}
        <Section>
          <InfoField
            label="Legal Name"
            value={company.name}
          />
          <InfoField
            label="Type"
            value={companyTypeLabel}
          />
          <InfoField
            label="Location"
            value={locationDisplay}
          />
          <InfoField
            label="Year Founded"
            value={company.yearFounded}
          />
          <InfoField
            fullWidth
            label="Description"
            value={company.description}
          />
        </Section>

        {/* Commercial-specific fields */}
        {isCommercial && (cmmcLabel || company.samsUeiNumber) && (
          <Section>
            <InfoField
              label="CMMC Level"
              value={cmmcLabel}
            />
            <InfoField
              label="SAMS UEI Number"
              value={company.samsUeiNumber}
            />
          </Section>
        )}

        {/* Previous Government Work */}
        {previousGovWorkCountries && (
          <Section>
            <InfoField
              fullWidth
              label="Previous Government Work"
              value={
                <BadgeList
                  items={previousGovWorkCountries.map((name) => ({ id: name, name }))}
                  size="sm"
                />
              }
            />
          </Section>
        )}
      </div>
    );
  };

  return (
    <div className="@container/technology-organization">
      <div className="grid grid-cols-1 gap-4 @min-[40.625rem]/technology-organization:grid-cols-2">
        {/* Column 1: Details */}
        <div className="flex flex-col gap-4">
          <Tile contentClassName="gap-0">
            <div className="flex items-center gap-3 pb-4">
              <CompanyLogoPreview
                className="h-14 w-14 shrink-0 rounded-xl"
                fallback={<Building2 className="size-5 text-gray-600" />}
                logoAlt={`${company.name} logo`}
                logoUrl={company.logoUrl}
              />
              <div className="flex min-w-0 flex-col">
                <span className="font-medium text-gray-900">{company.name}</span>
                {locationDisplay && <span className="text-sm text-gray-500">{locationDisplay}</span>}
              </div>
            </div>
            {renderDetailsContent()}
          </Tile>
        </div>

        {/* Column 2: Financial (if present) + Profile */}
        <div className="flex flex-col gap-4">
          {isCommercial && hasFinancialData && (
            <Tile header={{ title: 'Financial' }}>
              <div className="@container flex flex-col">
                <div className="flex gap-x-12 gap-y-4 justify-between flex-wrap w-full">
                  <InfoField
                    label="Revenue"
                    value={revenueDisplay}
                  />
                  <InfoField
                    label="Valuation"
                    value={valuationDisplay}
                  />
                  {company.isParentOrganization !== undefined && (
                    <InfoField
                      label="Parent Organization"
                      value={company.isParentOrganization ? 'Yes' : 'No'}
                    />
                  )}
                </div>
              </div>
            </Tile>
          )}

          <Tile header={{ title: 'Profile' }}>
            <div className="@container flex flex-col">
              {!hasUrls && <span className="text-sm text-gray-500">No profile links available.</span>}
              {hasUrls && (
                <div className="flex gap-x-12 gap-y-4 justify-between flex-wrap w-full">
                  {urlFields.map((field) => {
                    const url = field.value(company);
                    if (!url || !isValidUrl(url)) return null;
                    const normalizedUrl = toHttpsUrl(url);
                    return (
                      <InfoField
                        key={field.label}
                        label={field.label}
                        value={
                          <ExternalLink
                            className="max-w-full"
                            href={normalizedUrl}
                            iconClassName="shrink-0"
                            iconSize={16}
                            title={normalizedUrl}
                          >
                            {normalizedUrl}
                          </ExternalLink>
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </Tile>
        </div>
      </div>
    </div>
  );
};
